import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  TrendingUp, Eye, MousePointerClick, DollarSign, BarChart3,
  Users, Calendar, ArrowLeft, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminAdsAnalyticsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30);
  
  const [overview, setOverview] = useState(null);
  const [topCampaigns, setTopCampaigns] = useState([]);
  const [placementStats, setPlacementStats] = useState([]);
  const [timeSeries, setTimeSeries] = useState([]);
  const [topAdvertisers, setTopAdvertisers] = useState([]);

  useEffect(() => {
    fetchAllAnalytics();
  }, [period]);

  const fetchAllAnalytics = async () => {
    setLoading(true);
    try {
      const [overviewRes, campaignsRes, placementsRes, timeSeriesRes, advertisersRes] = await Promise.all([
        fetch(`${API_URL}/api/ads/admin/analytics/overview?days=${period}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/ads/admin/analytics/campaigns?limit=10`, { credentials: 'include' }),
        fetch(`${API_URL}/api/ads/admin/analytics/placements`, { credentials: 'include' }),
        fetch(`${API_URL}/api/ads/admin/analytics/time-series?days=${period}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/ads/admin/analytics/top-advertisers?limit=10`, { credentials: 'include' })
      ]);

      if (overviewRes.ok) {
        const data = await overviewRes.json();
        setOverview(data.overview);
      }

      if (campaignsRes.ok) {
        const data = await campaignsRes.json();
        setTopCampaigns(data.campaigns);
      }

      if (placementsRes.ok) {
        const data = await placementsRes.json();
        setPlacementStats(data.placements);
      }

      if (timeSeriesRes.ok) {
        const data = await timeSeriesRes.json();
        setTimeSeries(data.time_series);
      }

      if (advertisersRes.ok) {
        const data = await advertisersRes.json();
        setTopAdvertisers(data.advertisers);
      }
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="icon" onClick={() => navigate('/admin/marketing/ads')}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-2">
                <BarChart3 className="h-8 w-8 text-blue-600" />
                Advertising Analytics
              </h1>
              <p className="text-muted-foreground">Comprehensive performance insights</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={period}
              onChange={(e) => setPeriod(parseInt(e.target.value))}
              className="border rounded-md p-2"
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
            <Button variant="outline" onClick={fetchAllAnalytics}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {loading ? (
          <Card>
            <CardContent className="pt-6 text-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">Loading analytics...</p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Overview Stats */}
            {overview && (
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold">{overview.total_campaigns}</div>
                    <p className="text-sm text-muted-foreground">Paid Campaigns</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold text-purple-600">{overview.total_promotions}</div>
                    <p className="text-sm text-muted-foreground">System Promos</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold text-blue-600">
                      {overview.total_impressions.toLocaleString()}
                    </div>
                    <p className="text-sm text-muted-foreground">Impressions</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold text-green-600">
                      {overview.total_clicks.toLocaleString()}
                    </div>
                    <p className="text-sm text-muted-foreground">Clicks</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold text-orange-600">{overview.overall_ctr}%</div>
                    <p className="text-sm text-muted-foreground">Overall CTR</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold text-purple-600">
                      {overview.total_revenue_mtr.toLocaleString()}
                    </div>
                    <p className="text-sm text-muted-foreground">Revenue (MTR)</p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Placement Performance */}
            <Card>
              <CardHeader>
                <CardTitle>Placement Performance</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {placementStats.map((placement) => (
                    <div key={placement.placement} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-semibold capitalize">{placement.placement}</h3>
                        <Badge variant="outline">{placement.campaigns} campaigns</Badge>
                      </div>
                      <div className="grid grid-cols-4 gap-4 text-sm">
                        <div>
                          <div className="text-xs text-muted-foreground">Impressions</div>
                          <div className="font-semibold">{placement.impressions.toLocaleString()}</div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">Clicks</div>
                          <div className="font-semibold">{placement.clicks.toLocaleString()}</div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">CTR</div>
                          <div className="font-semibold text-orange-600">{placement.ctr}%</div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">Revenue</div>
                          <div className="font-semibold text-purple-600">
                            {placement.revenue_mtr.toLocaleString()} MTR
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Top Campaigns */}
            <Card>
              <CardHeader>
                <CardTitle>Top Performing Campaigns</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {topCampaigns.map((campaign, index) => (
                    <div key={campaign.id} className="border rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-bold text-lg">#{index + 1}</span>
                            <h4 className="font-semibold">{campaign.campaign_name}</h4>
                          </div>
                          <p className="text-sm text-muted-foreground">{campaign.business_name}</p>
                        </div>
                        <Badge className="capitalize">{campaign.placement}</Badge>
                      </div>
                      <div className="grid grid-cols-5 gap-4 mt-3 text-sm">
                        <div>
                          <div className="text-xs text-muted-foreground">Impressions</div>
                          <div className="font-semibold">{campaign.impressions.toLocaleString()}</div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">Clicks</div>
                          <div className="font-semibold">{campaign.clicks.toLocaleString()}</div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">CTR</div>
                          <div className="font-semibold text-orange-600">{campaign.ctr}%</div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">CPC</div>
                          <div className="font-semibold">{campaign.cpc.toLocaleString()} MTR</div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">ROI Score</div>
                          <div className="font-semibold text-green-600">{campaign.roi_score}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Top Advertisers */}
            <Card>
              <CardHeader>
                <CardTitle>Top Advertisers</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {topAdvertisers.map((advertiser, index) => (
                    <div key={advertiser.user_id} className="border rounded-lg p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-bold">#{index + 1}</span>
                            <span className="font-semibold">{advertiser.business_name}</span>
                          </div>
                          <p className="text-sm text-muted-foreground">{advertiser.user_email}</p>
                        </div>
                        <Badge variant="outline">{advertiser.campaigns} campaigns</Badge>
                      </div>
                      <div className="grid grid-cols-4 gap-4 text-sm">
                        <div>
                          <div className="text-xs text-muted-foreground">Total Spend</div>
                          <div className="font-semibold text-purple-600">
                            {advertiser.total_spend_mtr.toLocaleString()} MTR
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">Impressions</div>
                          <div className="font-semibold">{advertiser.impressions.toLocaleString()}</div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">Clicks</div>
                          <div className="font-semibold">{advertiser.clicks.toLocaleString()}</div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">Avg CTR</div>
                          <div className="font-semibold text-orange-600">{advertiser.ctr}%</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Time Series (Simplified) */}
            {timeSeries.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Performance Over Time</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {timeSeries.slice(-10).map((day) => (
                      <div key={day.date} className="flex items-center justify-between border-b pb-2">
                        <span className="text-sm font-medium">{day.date}</span>
                        <div className="flex gap-6 text-sm">
                          <span>
                            <Eye className="inline h-3 w-3 mr-1" />
                            {day.impressions.toLocaleString()}
                          </span>
                          <span>
                            <MousePointerClick className="inline h-3 w-3 mr-1" />
                            {day.clicks.toLocaleString()}
                          </span>
                          <span className="text-orange-600 font-semibold">{day.ctr}%</span>
                          <span className="text-purple-600 font-semibold">
                            {day.revenue_mtr.toLocaleString()} MTR
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
};
